import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SetupProgressTracker from "./SetupProgressTracker";

import {
  Home,
  Users,
  Sprout,
  Megaphone,
  HeartPulse,
  LineChart,
  Settings,
  LogOut,
  ShoppingCart,
  Command,
  CreditCard
} from "lucide-react";
import { apiClient } from "../api/api";

const Sidebar = (props) => {
  const { logout, user } = useAuth();
  const [profile, setProfile] = useState({
    name: "",
    role: "Brand Admin",
    photoUrl: null,
    companyType: user?.company_type || "" // Initialize from context to prevent flicker
  });
  const [brandName, setBrandName] = useState(() => {
    if (user && (user.shop_name || user.brand_name)) {
      return user.shop_name || user.brand_name;
    }
    return "";
  });
  const { sidebarOpen = false, setSidebarOpen, isDesktop = false } = props;
  const location = useLocation();

  const fetchUserProfile = async () => {
    try {
      const resp = await apiClient.get('/user/profile', { withCredentials: true });
      if (resp.data?.user) {
        const u = resp.data.user;
        setProfile({
          name: u.name || u.username || "User",
          role: u.role || "Admin",
          photoUrl: u.profile_photo_url,
          companyType: u.company_type
        });
      }
    } catch (err) {
      console.error("[Sidebar] Error fetching profile:", err);
    }
  };

  React.useEffect(() => {
    fetchUserProfile();
    if (user && (user.shop_name || user.brand_name)) {
      setBrandName(user.shop_name || user.brand_name);
      return;
    }
    const fetchBrandName = async () => {
      console.log("[Sidebar] fetchBrandName called. user:", user);
      try {
        const resp = await apiClient.get(`/get_brand_name`, {
          credentials: "include",
          withCredentials: true,
        });
        setBrandName(resp.data?.name || user?.email || "My Shop");
      } catch (err) {
        console.error("[Sidebar] Error fetching brand name:", err);
        setBrandName(user?.email || "My Shop");
      }
    };
    if (user && user.brand_id) {
      fetchBrandName();
    }
  }, [user]);

  const handleNavClick = () => {
    if (!isDesktop && setSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const styles = {
    sidebar: {
      width: "100%",
      maxWidth: "256px",
      backgroundColor: "#050505", // Very dark background
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      fontFamily: "system-ui, -apple-system, sans-serif",
      position: "relative",
      zIndex: 1000,
      overflowY: "auto",
      color: "#ffffff",
      padding: "16px",
    },
    headerSection: {
      padding: "5px 8px",
      marginBottom: "24px",
    },
    headerTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    },
    logoImage: {
      height: "50px",
      width: "auto",
      objectFit: "contain",
    },
    headerTitleSpan: {
      color: "#fff",
      background: "linear-gradient(to right, #ff6b8b, #ff9a8b)",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    nav: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    navItem: {
      display: "flex",
      alignItems: "center",
      padding: "12px 16px",
      fontSize: "15px",
      fontWeight: "500",
      borderRadius: "12px",
      textDecoration: "none",
      transition: "all 0.2s ease",
      cursor: "pointer",
      width: "100%",
      marginBottom: "4px",
    },
    navItemActive: {
      background: "linear-gradient(90deg, rgba(153, 64, 89, 0.5) 0%, rgba(153, 64, 89, 0.1) 100%)", // Red/Burgundy gradient
      color: "#ffffff",
      border: "1px solid rgba(153, 64, 89, 0.3)",
    },
    navItemInactive: {
      color: "#9ca3af", // Gray text
      backgroundColor: "transparent",
      border: "1px solid transparent",
    },
    navIcon: {
      width: "20px",
      height: "20px",
      marginRight: "16px",
    },
    bottomSection: {
      marginTop: "auto",
      borderTop: "1px solid #1f2937",
      paddingTop: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    userProfile: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      marginTop: "8px",
      borderRadius: "12px",
      // backgroundColor: "#1f2937",
    },
    avatar: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: "#374151",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
    userInfo: {
      display: "flex",
      flexDirection: "column",
    },
    userName: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#ffffff",
    },
    userRole: {
      fontSize: "12px",
      color: "#9ca3af",
    },
  };

  const navigationItems = [
    {
      id: "home",
      name: "Home",
      href: "/dashboard",
      icon: Home,
    },
    ...(profile.companyType === "Agency" || profile.role === "cs_internal" ? [{
      id: "command-centre",
      name: "Command Center",
      href: "/command-centre",
      icon: Command,
    }] : []),
    {
      id: "affiliates",
      name: "Affiliates",
      href: "/affiliates",
      icon: Users,
    },
    {
      id: "organic",
      name: "Organic",
      href: "/organic",
      icon: Sprout,
    },
    {
      id: "paid-ads",
      name: "Paid Ads",
      href: "/paid-ads",
      icon: Megaphone,
    },
    {
      id: "shop-health",
      name: "Shop Health",
      href: "/shop-health",
      icon: HeartPulse,
    },
    {
      id: "pnl",
      name: "P&L",
      href: "/pnl",
      icon: LineChart,
    },
    {
      id: "myshops",
      name: "My Shops",
      href: "/myshops",
      icon: ShoppingCart,
    },
    {
      id: "subscription",
      name: "Subscription",
      href: "/subscription",
      icon: CreditCard,
    },
  ];

  const handleNavItemHover = (e) => {
    if (!e.target.closest('[data-active="true"]')) {
      e.target.style.color = "#ffffff";
    }
  };

  const handleNavItemLeave = (e) => {
    if (!e.target.closest('[data-active="true"]')) {
      e.target.style.color = "#9ca3af";
    }
  };

  return (
    <div style={styles.sidebar}>
      {/* Mobile Close Button */}
      {!isDesktop && sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2002,
            background: "transparent",
            border: "none",
            color: "white",
            fontSize: 24,
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      )}

      {/* Header */}
      <div style={styles.headerSection}>
        <div style={styles.headerTitle}>
          <img src="/shopOS_Brand_Logo.png" alt="shopOS Logo" style={styles.logoImage} />
        </div>
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.id}
              to={item.href}
              style={{
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : styles.navItemInactive),
              }}
              data-active={isActive}
              onMouseEnter={handleNavItemHover}
              onMouseLeave={handleNavItemLeave}
              onClick={handleNavClick}
            >
              <Icon style={styles.navIcon} />
              {item.name}
            </Link>
          );
        })}
      </nav>
      <SetupProgressTracker />

      {/* Bottom Section */}
      <div style={styles.bottomSection}>
        {/* Setup Progress Tracker */}
        {/* <SetupProgressTracker /> */}
        
        <Link
          to="/settings"
          style={{
            ...styles.navItem,
            ...(location.pathname === "/settings"
              ? styles.navItemActive
              : styles.navItemInactive),
          }}
          onClick={handleNavClick}
        >
          <Settings style={styles.navIcon} />
          Settings
        </Link>
        <button
          onClick={logout}
          style={{
            ...styles.navItem,
            ...styles.navItemInactive,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <LogOut style={styles.navIcon} />
          Logout
        </button>
        <div style={styles.userProfile}>
          <div style={styles.avatar}>
            {profile.photoUrl ? (
              <img 
                src={profile.photoUrl}
                alt="User"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {e.target.style.display = 'none'}} 
              />
            ) : (
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#ff6b8b' }}>
                {profile.name ? profile.name[0].toUpperCase() : 'U'}
              </span>
            )}
          </div>
          <div style={styles.userInfo}>
            <span style={styles.userName}>{profile.name || "User"}</span>
            <span style={styles.userRole}>{profile.role}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
